import { defineComponent as _defineComponent } from 'vue'
import FormLayout from '../common/FormLayout.vue'
import CreatedInfo from '../common/CreatedInfo.vue'
import JobCard from '@/components/jobs/job-search-page/JobCard.vue'
import ProjectProgressBar from './ProjectProgressBar.vue'
import VsWrapper from '../vision/VsWrapper.vue'
import ProjectFilter from '@/components/project/ProjectFilter'
import Project from '@/models/project'
import { useStore } from 'vuex-composition-helpers'
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import {
  ACTIVE,
  ASSIGNED_TO_SERVICE_PROVIDER,
  CANCELLED,
  COMPLETED,
  PAUSED,
  REQUESTED,
  SCHEDULED,
} from '@/models/job'
import {
  onBeforeRouteUpdate,
  useRoute,
  useRouter,
} from 'vue-router/composables'
import { usePagination } from '@/components/jobs/pagination'
import { useJobsQuery } from '@/components/jobs/query'
import { useJobsSearch } from '@/components/jobs/search'
import { useProjectViewFilters } from '@/components/jobs/filters'
import { useBulkUpdate } from '@/components/jobs/bulk-update'
import BulkUpdateForm from '@/components/jobs/BulkUpdateForm.vue'
import RightDrawer from '@/components/common/RightDrawer.vue'
import { useJobStatuses } from '@/components/project/jobstatus'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import VsButton from '@/components/vision/VsButton.vue'
import JobSearchPagination from '@/components/jobs/job-search-page/JobSearchPagination.vue'
import { JobSearchResponse } from '@/models/types/job-search-response'
import VsSnackbar from '@/components/vision/VsSnackbar.vue'
import VsBottomSheet from '@/components/vision/VsBottomSheet.vue'

interface Props {
  id: string
  parentRouteName: string
  parentRouteParams?: Record<string, string>
}


export default /*#__PURE__*/_defineComponent({
  __name: 'Project',
  props: {
    id: null,
    parentRouteName: null,
    parentRouteParams: { default: () => ({}) }
  },
  setup(__props: any) {

const props = __props as { id: string, parentRouteName: string, parentRouteParams: Record<string, string> }

const orderedStatuses = ['completed', 'active', 'unstarted']



const store = useStore()
const route = useRoute()
const router = useRouter()

const jobSideSheetUrl = computed(() =>
  route.name?.includes('ticket-info-panel')
    ? route.name!
    : `${route.name}-ticket-info-panel`
)

// Local Data/State
const projectName = ref<string>('')
const snackbarMessage = ref<string | null>(null)
const selectedFilters = ref<{ id: string; name: string }[]>([])
const isBulkUpdateFormOpen = ref(false)
const isBulkUpdateBottomSheetOpen = ref(false)

// Paginated Fetch Jobs API
const bulkUpdateEnabled = useBulkUpdate()
const filters = useProjectViewFilters(props.id, selectedFilters)
const { getCurrentPage, updatePage } = usePagination()
const itemPerPage = ref(20)
const query = reactive(
  useJobsQuery(
    filters,
    getCurrentPage,
    itemPerPage,
    ref(['requested_start', 'number']), // sortBy
    ref([false, false]) // sortDesc
  )
)
const { jobs: projectJobs, isLoading, reFetch } = useJobsSearch(query)

// --------------------------------------Lifecycle hooks------------------------------------------
onMounted(async () => {
  !project.value && (await fetchProject(props.id))
  projectName.value = project.value.name
  selectedFilters.value = jobStatuses.value.filter(
    (s) => s.id !== CANCELLED && s.id !== COMPLETED
  )
})
onBeforeRouteUpdate(async (to, from, next) => {
  if (from.params.id === 'new' && !to.params.id) {
    await Promise.all([
      fetchJobs(),
      fetchProject(props.id), // Fetch project to update the job status counter
    ])
    selectedJobs.value = projectJobs.value.filter(
      (job) =>
        selectedJobs.value.map((j) => j.number).includes(job.number) &&
        selectedFilters.value.map((f) => f.id).includes(job.status)
    )
  }
  next()
})
onBeforeUnmount(() => {
  selectedJobs.value = []
  clearError()
})

watch(Object.values(filters), () => {
  updatePage(1)
})
// ----------------------------------End Lifecycle hooks------------------------------------------

// -------------------------------------------Vuex------------------------------------------------
const saving = computed<boolean>(() => store.state.projects.saving)
const loading = computed<boolean>(() => store.state.projects.loading)
const error = computed(() => store.state.projects.error)
const jobIsSaving = computed<boolean>(() => store.state.jobs.saving)
watch(jobIsSaving, async (isSaving, wasSaving) => {
  if (wasSaving && !isSaving) {
    await Promise.all([
      fetchJobs(),
      fetchProject(props.id), // Fetch project to update the job status counter
    ])
    selectedJobs.value = projectJobs.value.filter(
      (job) =>
        selectedJobs.value.map((j) => j.number).includes(job.number) &&
        selectedFilters.value.map((f) => f.id).includes(job.status)
    )
  }
})

const project = computed<Project>(() => store.state.projects.all[props.id])
const selectedJobs = ref<JobSearchResponse[]>([])
const jobStatuses = useJobStatuses()

// Vuex Actions
const fetchProject = async (id: string) => {
  await store.dispatch('projects/fetchProject', id)
}
const updateProject = async (obj: {
  projectId: string
  projectName: string
}) => {
  await store.dispatch('projects/updateProject', obj)
}
// End Vuex Actions

// Vuex Mutations
const clearError = () => {
  store.commit('projects/CLEAR_ERROR_PROJECT')
}
// End Vuex Mutations

// -------------------------------------------End Vuex--------------------------------------------

// --------------------------------------Computed properties--------------------------------------
const filterOptions = computed(() =>
  jobStatuses.value.map((s) => ({
    ...s,
    count: jobStatusCount.value[s.id] || 0,
  }))
)

const jobStatusCount = computed(() => ({
  [REQUESTED]: project.value.requestedJobCount,
  [ASSIGNED_TO_SERVICE_PROVIDER]:
    project.value.assignedToServiceProviderJobCount,
  [SCHEDULED]: project.value.scheduledJobCount,
  [ACTIVE]: project.value.activeJobCount,
  [PAUSED]: project.value.pausedJobCount,
  [COMPLETED]: project.value.completedJobCount,
  [CANCELLED]: project.value.cancelledJobCount,
}))
const jobCountByStatus = computed(() => {
  const jobCountByStatus = {
    completed: project.value.completedJobCount,
    active: project.value.activeJobCount + project.value.pausedJobCount,
    unstarted:
      project.value.requestedJobCount +
      project.value.scheduledJobCount +
      project.value.assignedToServiceProviderJobCount,
  }

  return orderedStatuses.map((status) => ({
    count: jobCountByStatus[status] || 0,
    name: status,
  }))
})

const hasChanges = computed(() => projectName.value !== project.value.name)
const isJobFormVisible = computed(
  () => route.name?.includes('ticket-info-panel') ?? false
)
const updateJobsLabel = computed(() => {
  if (selectedJobs.value.length === 1) return 'Update job'
  return `Update ${selectedJobs.value.length} jobs`
})
const showCheckbox = computed(() => selectedJobs.value.length > 0)
const areAllJobsSelected = computed(() => {
  const selectedJobIds = selectedJobs.value.map((j) => j.number)
  for (const job of projectJobs.value) {
    if (!selectedJobIds.includes(job.number)) {
      return false
    }
  }
  return true
})
const selectAllButtonLabel = computed(() =>
  areAllJobsSelected.value ? 'Deselect all' : 'Select all'
)
// ----------------------------------End Computed properties--------------------------------------

// ----------------------------------------Methods------------------------------------------------
async function fetchJobs() {
  await reFetch()
}

function setProjectName(newName: string) {
  projectName.value = newName
}

async function save() {
  await updateProject({
    projectId: props.id,
    projectName: projectName.value,
  })
  snackbarMessage.value = 'Project saved'
}

function discard() {
  projectName.value = project.value.name
}

function dismiss() {
  router.push({
    name: props.parentRouteName,
    params: props.parentRouteParams,
  })
}

function clearSnackbarMessage() {
  snackbarMessage.value = null
}

function clearSnackbarError() {
  clearError()
}

function openNewJobSideSheet() {
  router.push({
    name: `${route.name}-ticket-info-panel`,
    params: {
      id: 'new',
      hubId: project.value.ownerId,
    },
  })
}

function isJobSelected(job: JobSearchResponse) {
  return selectedJobs.value.map((job) => job.number).includes(job.number)
}

function handleSelectDeselectAll() {
  if (areAllJobsSelected.value) {
    selectedJobs.value = []
  } else {
    projectJobs.value.forEach((job) => {
      if (!selectedJobs.value.map((j) => j.number).includes(job.number)) {
        selectedJobs.value.push(job)
      }
    })
  }
}

async function handleBulkUpdate(updatedJobs: JobSearchResponse[]) {
  await Promise.all([
    fetchJobs(),
    fetchProject(props.id), // Fetch project to update the job status counter
  ])
  selectedJobs.value = updatedJobs.filter((job) =>
    selectedFilters.value.map((f) => f.id).includes(job.status)
  )
}

async function handleJobSaved(newJob: { jobNumber: number | null }) {
  await Promise.all([
    fetchJobs(),
    fetchProject(props.id), // Fetch project to update the job status counter
  ])
  selectedJobs.value = projectJobs.value.filter(
    (job) =>
      selectedJobs.value.map((j) => j.number).includes(job.number) &&
      job.number !== newJob.jobNumber &&
      selectedFilters.value.map((f) => f.id).includes(job.status)
  )
}

// -------------------------------------End Methods------------------------------------------------

return { __sfc: true,orderedStatuses, props, store, route, router, jobSideSheetUrl, projectName, snackbarMessage, selectedFilters, isBulkUpdateFormOpen, isBulkUpdateBottomSheetOpen, bulkUpdateEnabled, filters, getCurrentPage, updatePage, itemPerPage, query, projectJobs, isLoading, reFetch, saving, loading, error, jobIsSaving, project, selectedJobs, jobStatuses, fetchProject, updateProject, clearError, filterOptions, jobStatusCount, jobCountByStatus, hasChanges, isJobFormVisible, updateJobsLabel, showCheckbox, areAllJobsSelected, selectAllButtonLabel, fetchJobs, setProjectName, save, discard, dismiss, clearSnackbarMessage, clearSnackbarError, openNewJobSideSheet, isJobSelected, handleSelectDeselectAll, handleBulkUpdate, handleJobSaved, FormLayout, CreatedInfo, JobCard, ProjectProgressBar, VsWrapper, ProjectFilter, BulkUpdateForm, RightDrawer, VsTextInput, VsButton, JobSearchPagination, VsSnackbar, VsBottomSheet }
}

})